import { useRef } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import type { DesignerCanvasClass, DesignerExemplar, DesignerExemplarWritePayload } from '../types/designer-exemplar.types';
import { DesignerExemplarForm } from './designer-exemplar-form';
import type { EntitySheetMode } from './vertical-profile-sheet';

interface DesignerExemplarSheetProps {
  open: boolean;
  mode: EntitySheetMode;
  exemplar: DesignerExemplar | null;
  canvasClasses: DesignerCanvasClass[] | undefined;
  isSaving?: boolean;
  isRetiring?: boolean;
  submitError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: DesignerExemplarWritePayload) => void | Promise<void | DesignerExemplar>;
  onRetire?: () => void | Promise<void>;
  onExemplarUpdated?: (exemplar: DesignerExemplar) => void;
}

export function DesignerExemplarSheet({
  open,
  mode,
  exemplar,
  canvasClasses,
  isSaving = false,
  isRetiring = false,
  submitError = null,
  onOpenChange,
  onSubmit,
  onRetire,
  onExemplarUpdated,
}: DesignerExemplarSheetProps) {
  const isCreate = mode === 'create';
  const formSessionKey = useRef('closed');
  if (!open) {
    formSessionKey.current = 'closed';
  } else if (formSessionKey.current === 'closed') {
    formSessionKey.current = exemplar?.id ?? 'create';
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-4xl">
        <div className="border-b border-gray-400 px-6 py-5">
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className="text-xl font-medium text-black">
              {isCreate ? 'New exemplar' : exemplar?.name ?? 'Exemplar'}
            </SheetTitle>
            <SheetDescription className="text-sm text-gray-500">
              Manage Remi reference creatives for the designer agent corpus.
            </SheetDescription>
          </SheetHeader>
          {!isCreate && exemplar ? (
            <dl className="mt-4 grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
              <div>
                <dt className="text-xs font-medium text-gray-500">Canvas class</dt>
                <dd className="mt-0.5 text-black">{exemplar.canvas_class}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Vertical</dt>
                <dd className="mt-0.5 text-black">{exemplar.vertical}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Status</dt>
                <dd className="mt-0.5 capitalize text-black">{exemplar.extraction_status}</dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-gray-500">Updated</dt>
                <dd className="mt-0.5 text-black">{formatDisplayDateTime(exemplar.updated_at)}</dd>
              </div>
            </dl>
          ) : null}
        </div>

        <div className="flex-1 px-6 py-5">
          {open ? (
            <DesignerExemplarForm
              key={formSessionKey.current}
              initialExemplar={isCreate ? null : exemplar}
              canvasClasses={canvasClasses}
              isCreate={isCreate}
              isSaving={isSaving}
              isRetiring={isRetiring}
              submitError={submitError}
              onSubmit={onSubmit}
              onCancel={() => onOpenChange(false)}
              onRetire={onRetire}
              onExemplarUpdated={onExemplarUpdated}
            />
          ) : null}
        </div>

        {isSaving ? (
          <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-400 bg-black px-6 py-3 text-sm text-white" role="status">
            <Loader2 className="size-4 animate-spin" />
            Saving…
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
